import React, { useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Badge,
  Drawer,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import AppButton from "../buttons/AppButton";

export interface PageToolbarProps {
  searchTerm?: string;
  onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchPlaceholder?: string;
  hideSearch?: boolean;
  actionButtons?: React.ReactNode;
  children?: React.ReactNode;
}

const PageToolbar: React.FC<PageToolbarProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder = "Search...",
  hideSearch = false,
  actionButtons,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Count how many filters exist to display on the badge
  const filterCount = React.Children.count(children);

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        sx={{
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          mb: 3,
          position: "sticky",
          top: 0,
          zIndex: 10,
          p: 1.5,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        {/* TOP BAR / LEFT SIDE */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", width: "100%", overflow: "hidden" }}
        >
          {!hideSearch && (
            <TextField
              placeholder={searchPlaceholder}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={onSearchChange}
              fullWidth={isMobile}
              sx={{ minWidth: { md: 240 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}

          {/* MOBILE FILTER TOGGLE BUTTON */}
          {isMobile && filterCount > 0 && (
            <IconButton
              onClick={() => setMobileDrawerOpen(true)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.7,
              }}
            >
              <Badge badgeContent={filterCount} color="primary">
                <FilterListIcon fontSize="small" />
              </Badge>
            </IconButton>
          )}

          {/* DESKTOP FILTERS (Inline horizontal scroll when many filters) */}
          {!isMobile && children && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "nowrap",
                overflowX: "auto",

                py: 0.5,
                scrollbarWidth: "none", // Firefox
                "&::-webkit-scrollbar": {
                  display: "none", // Chrome, Safari, Edge
                },
              }}
            >
              {children}
            </Box>
          )}
        </Stack>

        {/* RIGHT SIDE / ACTION BUTTONS */}
        {actionButtons && (
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", md: "flex-end" },
              minWidth: "fit-content",
            }}
          >
            {actionButtons}
          </Box>
        )}
      </Stack>

      {/* MOBILE FILTER BOTTOM SHEET DRAWER */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: "75vh",
                p: 2.5,
              },
            },
          }}
        >
          <Stack
            direction="row"
            sx={{
              mb: 1.5,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Filters
            </Typography>
            <IconButton onClick={() => setMobileDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {/* All page filters stacked neatly inside the drawer */}
          <Stack spacing={2} sx={{ overflowY: "auto", pb: 2 }}>
            {children}
          </Stack>

          <AppButton
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setMobileDrawerOpen(false)}
            sx={{ mt: 1 }}
          >
            Apply Filters
          </AppButton>
        </Drawer>
      )}
    </>
  );
};

export default PageToolbar;
